import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { AccountInfo } from '@azure/msal-browser';
import { SignInSplash } from '@/components/auth/SignInSplash';
import { useGetMe, useUpdateMe, getGetMeQueryKey } from '@/api/auth/self/self';
import {
  UserProfile as ApiUserProfile,
  UpdateUserProfileRequest,
} from '@/api/auth/missionImpactAuthAPI.schemas';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/components/theme-provider';
import { useQueryClient } from '@tanstack/react-query';
import { logger } from '@/services/logger';

interface UserProfile {
  givenName: string;
  surname: string;
}

interface UserContextType {
  account: AccountInfo;
  error: Error | null;
  userProfile: UserProfile | null;
  meProfile: ApiUserProfile | null;
  isLoading: boolean;
  updateUserProfile: (profile: UserProfile) => void;
  clearUserProfile: () => void;
  updateUserSettings: (settings: UpdateUserProfileRequest) => Promise<void>;
  isUpdatingSettings: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

/**
 * Provider component that manages user profile and settings
 * Must be used inside ProvisioningProvider
 */
export function UserProvider({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();
  const { setLanguage, rehydrateFromApi: rehydrateLanguage } = useLanguage();
  const { setTheme, rehydrateFromApi: rehydrateTheme } = useTheme();
  const queryClient = useQueryClient();
  const [error] = useState<Error | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  const account = accounts[0];

  const getCacheKey = useCallback((suffix: string) => {
    return `datamission_${account.homeAccountId}_${suffix}`;
  }, [account.homeAccountId]);

  const getCachedProfile = useCallback((): UserProfile | null => {
    const key = getCacheKey('profile');
    try {
      const cached = localStorage.getItem(key);
      if (cached) {
        return JSON.parse(cached) as UserProfile;
      }
    } catch (err) {
      logger.warn('Failed to parse cached profile', {
        context: 'UserContext.getCachedProfile',
        error: err instanceof Error ? err.message : String(err),
      });
    }
    return null;
  }, [getCacheKey]);

  const updateUserProfile = useCallback((profile: UserProfile) => {
    const key = getCacheKey('profile');
    try {
      localStorage.setItem(key, JSON.stringify(profile));
      setUserProfile(profile);
      logger.info('User profile updated in cache', {
        context: 'UserContext.updateUserProfile',
      });
    } catch (err) {
      logger.error(err instanceof Error ? err : new Error('Failed to cache profile'), {
        context: 'UserContext.updateUserProfile',
      });
    }
  }, [getCacheKey]);

  const clearUserProfile = useCallback(() => {
    const key = getCacheKey('profile');
    localStorage.removeItem(key);
    setUserProfile(null);
  }, [getCacheKey]);

  // Fetch /me profile
  const { data: meResponse, isLoading: isMeLoading } = useGetMe();
  const meProfile = meResponse?.data ?? null;

  const updateMeMutation = useUpdateMe();

  const updateUserSettings = async (settings: UpdateUserProfileRequest): Promise<void> => {
    if (settings.preferredLanguage) {
      setLanguage(settings.preferredLanguage);
    }
    if (settings.theme) {
      setTheme(settings.theme);
    }

    await updateMeMutation.mutateAsync({ data: settings });
    await queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });

    logger.info('User settings updated', {
      context: 'UserContext.updateUserSettings',
      preferredLanguage: settings.preferredLanguage,
      theme: settings.theme,
    });
  };

  // Rehydrate language/theme when meProfile loads
  useEffect(() => {
    if (meProfile?.onboarded) {
      if (meProfile.preferredLanguage) {
        rehydrateLanguage(meProfile.preferredLanguage);
      }
      if (meProfile.theme) {
        rehydrateTheme(meProfile.theme);
      }
    }
  }, [meProfile?.onboarded, meProfile?.preferredLanguage, meProfile?.theme, rehydrateLanguage, rehydrateTheme]);

  // Load cached profile on mount
  useEffect(() => {
    const cached = getCachedProfile();
    if (cached) {
      const allAccounts = instance.getAllAccounts();
      if (allAccounts.length > 0) {
        const currentAccount = allAccounts[0];
        const tokenGivenName = currentAccount.idTokenClaims?.given_name as string | undefined;
        const tokenSurname = currentAccount.idTokenClaims?.family_name as string | undefined;

        if (cached.givenName === tokenGivenName && cached.surname === tokenSurname) {
          clearUserProfile();
        } else {
          setUserProfile(cached);
        }
      } else {
        setUserProfile(cached);
      }
    }
  }, [getCachedProfile, clearUserProfile, instance]);

  // Set user context in Application Insights
  useEffect(() => {
    logger.setUser(account.homeAccountId, account.tenantId);
    logger.trackEvent('UserSession.Started', {
      accountId: account.homeAccountId,
      username: account.username,
      environment: import.meta.env.MODE,
    });
  }, [account.homeAccountId, account.tenantId, account.username]);

  if (isMeLoading) {
    return <SignInSplash />;
  }

  return (
    <UserContext.Provider
      value={{
        account,
        error,
        userProfile,
        meProfile,
        isLoading: isMeLoading,
        updateUserProfile,
        clearUserProfile,
        updateUserSettings,
        isUpdatingSettings: updateMeMutation.isPending,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

/**
 * Hook to access user context
 */
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    // eslint-disable-next-line no-restricted-syntax
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
